import React, { useState, useEffect } from 'react';
import { Shield, Loader2, ToggleLeft, ToggleRight, Plus, Trash2, Wifi, WifiOff, Users, LogOut, Send, Lock, AlertTriangle } from 'lucide-react';
import { API } from '../../App';

function McpPolicies({ wsId }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toolName, setToolName] = useState('');
  const [rateMin, setRateMin] = useState('');
  const [rateHour, setRateHour] = useState('');
  const [adding, setAdding] = useState(false);

  const load = () => {
    API.get(`/api/workspaces/${wsId}/mcp-policies`)
      .then(r => setPolicies(r.data.policies || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [wsId]);

  const addPolicy = async () => {
    if (!toolName.trim()) return;
    setAdding(true);
    try {
      await API.put(`/api/workspaces/${wsId}/mcp-policies/${encodeURIComponent(toolName.trim())}`, {
        enabled: true,
        rate_limit_per_minute: rateMin ? parseInt(rateMin) : null,
        rate_limit_per_hour: rateHour ? parseInt(rateHour) : null,
      });
      setToolName('');
      setRateMin('');
      setRateHour('');
      load();
    } catch {}
    setAdding(false);
  };

  const togglePolicy = async (p) => {
    await API.put(`/api/workspaces/${wsId}/mcp-policies/${encodeURIComponent(p.tool_name)}`, { enabled: !p.enabled });
    load();
  };

  const removePolicy = async (name) => {
    await API.delete(`/api/workspaces/${wsId}/mcp-policies/${encodeURIComponent(name)}`);
    load();
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-bold flex items-center gap-2"><Wifi className="w-4 h-4 text-[#FF4D00]" /> MCP Tool Policies</h3>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={toolName} onChange={e => setToolName(e.target.value)}
          placeholder="Tool name (e.g. debate)" className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
        <input value={rateMin} onChange={e => setRateMin(e.target.value)} type="number"
          placeholder="Rate/min" className="w-24 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
        <input value={rateHour} onChange={e => setRateHour(e.target.value)} type="number"
          placeholder="Rate/hr" className="w-24 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
        <button onClick={addPolicy} disabled={adding || !toolName.trim()}
          className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 flex items-center gap-2 min-h-[44px]">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {policies.map(p => (
        <div key={p.id || p.tool_name} className="flex items-center gap-3 p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
          {p.enabled ? <Wifi className="w-4 h-4 text-[#22C55E] flex-shrink-0" /> : <WifiOff className="w-4 h-4 text-[#EF4444] flex-shrink-0" />}
          <span className="text-sm font-mono flex-1">{p.tool_name}</span>
          {p.rate_limit_per_minute && <span className="text-xs text-[#A0A0A0]">{p.rate_limit_per_minute}/min</span>}
          {p.rate_limit_per_hour && <span className="text-xs text-[#A0A0A0]">{p.rate_limit_per_hour}/hr</span>}
          <button onClick={() => togglePolicy(p)} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
            {p.enabled ? <ToggleRight className="w-5 h-5 text-[#FF4D00]" /> : <ToggleLeft className="w-5 h-5 text-[#A0A0A0]" />}
          </button>
          <button onClick={() => removePolicy(p.tool_name)} className="p-2 text-[#A0A0A0] hover:text-[#EF4444] min-w-[44px] min-h-[44px] flex items-center justify-center">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      {policies.length === 0 && <p className="text-xs text-[#A0A0A0] text-center py-4">No custom MCP policies — all tools use defaults</p>}
    </div>
  );
}

function SessionManager({ wsId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const load = () => {
    API.get(`/api/workspaces/${wsId}/sessions`)
      .then(r => { setSessions(r.data.sessions || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [wsId]);

  const forceLogout = async (sessionIds, userId, all) => {
    const msg = all ? 'Force logout ALL workspace sessions?' : userId ? 'Force logout all sessions for this user?' : 'Force logout selected session?';
    if (!window.confirm(msg)) return;
    await API.post(`/api/workspaces/${wsId}/sessions/force-logout`, {
      session_ids: sessionIds || [], user_id: userId || undefined, logout_all: all || false,
    });
    load();
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold flex items-center gap-2"><Users className="w-4 h-4 text-[#FF4D00]" /> Active Sessions ({total})</h3>
        {sessions.length > 0 && (
          <button onClick={() => forceLogout(null, null, true)}
            className="px-3 py-1.5 bg-[#EF4444] text-white text-xs font-medium min-h-[44px] flex items-center gap-1">
            <LogOut className="w-3 h-3" /> Logout All
          </button>
        )}
      </div>
      {sessions.length === 0 ? (
        <p className="text-xs text-[#A0A0A0] text-center py-4">No active sessions</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {sessions.map(s => (
            <div key={s.id} className="flex items-center gap-3 p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{s.name || s.email}</p>
                <p className="text-xs text-[#A0A0A0]">Last active: {s.last_active_at ? new Date(s.last_active_at).toLocaleString() : 'N/A'}</p>
              </div>
              <button onClick={() => forceLogout([s.id])}
                className="p-2 text-[#A0A0A0] hover:text-[#EF4444] min-w-[44px] min-h-[44px] flex items-center justify-center">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MarketplaceFirewall({ wsId }) {
  const [mode, setMode] = useState('approval_required');
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get(`/api/workspaces/${wsId}/marketplace-policy`)
      .then(r => { setMode(r.data.marketplace_mode || 'approval_required'); setPublishing(r.data.publishing_enabled || false); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [wsId]);

  const save = async () => {
    setSaving(true);
    await API.put(`/api/workspaces/${wsId}/marketplace-policy`, { marketplace_mode: mode, publishing_enabled: publishing });
    setSaving(false);
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-bold flex items-center gap-2"><Shield className="w-4 h-4 text-[#FF4D00]" /> Marketplace Firewall</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {['disabled', 'browse_only', 'approval_required', 'allowlist_only'].map(m => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-3 py-2 text-xs font-mono min-h-[44px] ${mode === m ? 'bg-[#FF4D00] text-black' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0]'}`}>
            {m.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
        <span className="text-sm">Allow publishing to marketplace</span>
        <button onClick={() => setPublishing(!publishing)} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
          {publishing ? <ToggleRight className="w-5 h-5 text-[#FF4D00]" /> : <ToggleLeft className="w-5 h-5 text-[#A0A0A0]" />}
        </button>
      </div>
      <button onClick={save} disabled={saving}
        className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 min-h-[44px]">
        {saving ? 'Saving...' : 'Save Policy'}
      </button>
    </div>
  );
}

function SiemConfig({ wsId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [active, setActive] = useState(true);
  const [events, setEvents] = useState('*');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    API.get(`/api/workspaces/${wsId}/siem`)
      .then(r => {
        const c = r.data.siem_config;
        setConfig(c);
        if (c) { setUrl(c.webhook_url || ''); setActive(c.is_active !== false); setEvents((c.event_types || ['*']).join(', ')); }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [wsId]);

  const save = async () => {
    setSaving(true);
    setResult(null);
    try {
      const r = await API.put(`/api/workspaces/${wsId}/siem`, {
        webhook_url: url, is_active: active,
        event_types: events.split(',').map(s => s.trim()).filter(Boolean),
      });
      if (r.data.secret_token) {
        setResult({ secret: r.data.secret_token });
      }
      load();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Failed' });
    }
    setSaving(false);
  };

  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await API.post(`/api/workspaces/${wsId}/siem/test`);
      setResult({ test: r.data.status === 'sent' ? `Sent (${r.data.response_code})` : `Failed: ${r.data.error}` });
    } catch (e) {
      setResult({ error: 'Test failed' });
    }
    setTesting(false);
  };

  const remove = async () => {
    if (!window.confirm('Remove SIEM webhook?')) return;
    await API.delete(`/api/workspaces/${wsId}/siem`);
    setConfig(null);
    setUrl('');
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-bold flex items-center gap-2"><Send className="w-4 h-4 text-[#FF4D00]" /> SIEM Webhook</h3>
      <input value={url} onChange={e => setUrl(e.target.value)}
        placeholder="https://your-siem.example.com/webhook" className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm font-mono focus:outline-none focus:border-[#FF4D00]" />
      <input value={events} onChange={e => setEvents(e.target.value)}
        placeholder="Event types (comma-separated, * for all)" className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
      <div className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
        <span className="text-sm">Active</span>
        <button onClick={() => setActive(!active)} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
          {active ? <ToggleRight className="w-5 h-5 text-[#FF4D00]" /> : <ToggleLeft className="w-5 h-5 text-[#A0A0A0]" />}
        </button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={save} disabled={saving || !url.trim()}
          className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 min-h-[44px]">
          {saving ? 'Saving...' : 'Save'}
        </button>
        {config && (
          <>
            <button onClick={test} disabled={testing}
              className="px-4 py-2 bg-[#2A2A2A] text-[#F5F5F5] text-sm font-medium disabled:opacity-50 min-h-[44px]">
              {testing ? 'Testing...' : 'Send Test'}
            </button>
            <button onClick={remove}
              className="px-4 py-2 border border-[#EF4444]/30 text-[#EF4444] text-sm font-medium min-h-[44px]">
              Remove
            </button>
          </>
        )}
      </div>
      {result && (
        <div className={`p-2 text-xs font-mono ${result.error ? 'text-[#EF4444]' : 'text-[#22C55E]'}`}>
          {result.error || result.test || (result.secret ? `Secret token (save now — shown once): ${result.secret}` : '')}
        </div>
      )}
      {config?.last_delivery_at && (
        <p className="text-xs text-[#A0A0A0]">
          Last delivery: {new Date(config.last_delivery_at).toLocaleString()} — {config.last_delivery_status}
          {config.failure_count > 0 && <span className="text-[#EF4444]"> ({config.failure_count} failures)</span>}
        </p>
      )}
    </div>
  );
}

function MfaPolicy({ wsId }) {
  const [mfaRequired, setMfaRequired] = useState(false);
  const [deferSso, setDeferSso] = useState(false);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get(`/api/workspaces/${wsId}/mfa-policy`),
      API.get(`/api/workspaces/${wsId}/mfa-compliance`).catch(() => ({ data: null })),
    ]).then(([pol, comp]) => {
      setMfaRequired(pol.data.mfa_required || false);
      setDeferSso(pol.data.mfa_defer_to_sso || false);
      setCompliance(comp.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [wsId]);

  const save = async () => {
    setSaving(true);
    await API.put(`/api/workspaces/${wsId}/mfa-policy`, { mfa_required: mfaRequired, mfa_defer_to_sso: deferSso });
    setSaving(false);
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-bold flex items-center gap-2"><Lock className="w-4 h-4 text-[#FF4D00]" /> MFA Enforcement</h3>
      <div className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
        <div>
          <p className="text-sm">Require MFA for all members</p>
          <p className="text-xs text-[#A0A0A0]">Non-compliant members will be prompted to set up TOTP</p>
        </div>
        <button onClick={() => setMfaRequired(!mfaRequired)} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
          {mfaRequired ? <ToggleRight className="w-5 h-5 text-[#FF4D00]" /> : <ToggleLeft className="w-5 h-5 text-[#A0A0A0]" />}
        </button>
      </div>
      <div className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
        <div>
          <p className="text-sm">Defer to SSO provider</p>
          <p className="text-xs text-[#A0A0A0]">Trust SSO provider's MFA enforcement</p>
        </div>
        <button onClick={() => setDeferSso(!deferSso)} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
          {deferSso ? <ToggleRight className="w-5 h-5 text-[#FF4D00]" /> : <ToggleLeft className="w-5 h-5 text-[#A0A0A0]" />}
        </button>
      </div>
      <button onClick={save} disabled={saving}
        className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 min-h-[44px]">
        {saving ? 'Saving...' : 'Save Policy'}
      </button>

      {compliance && (
        <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold">Compliance: {compliance.compliance_rate}%</span>
            <span className="text-xs text-[#A0A0A0]">{compliance.compliant}/{compliance.total_members} members</span>
          </div>
          <div className="h-2 bg-[#2A2A2A] mb-3">
            <div className="h-full bg-[#22C55E]" style={{ width: `${compliance.compliance_rate}%` }} />
          </div>
          {compliance.non_compliant_users?.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-[#EF4444] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Non-compliant:</p>
              {compliance.non_compliant_users.map(u => (
                <div key={u.id} className="flex items-center gap-2 text-xs text-[#A0A0A0]">
                  <span className="truncate">{u.name || u.email}</span>
                  <span className="text-[#FF4D00]/60 font-mono">{u.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SecuritySection({ wsId }) {
  return (
    <div className="space-y-8">
      <McpPolicies wsId={wsId} />
      <hr className="border-[#2A2A2A]" />
      <SessionManager wsId={wsId} />
      <hr className="border-[#2A2A2A]" />
      <MarketplaceFirewall wsId={wsId} />
      <hr className="border-[#2A2A2A]" />
      <SiemConfig wsId={wsId} />
      <hr className="border-[#2A2A2A]" />
      <MfaPolicy wsId={wsId} />
    </div>
  );
}
